import React, { useEffect, useState, useMemo } from 'react';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TablePagination,
	TableFooter,
	TableRow,
	CircularProgress,
	Typography,
	Paper,
	Grid,
	Stack,
} from '@mui/material';
import { format } from 'date-fns';
import { LoadQuoteButton } from '../LoadQuoteButton';

import { Order, QuoteData, createQuoteRow, stableSort, getComparator } from './data';
import { TablePaginationActions } from './tablePagination';
import { QuoteTableHead } from './tableHead';
import { colours } from '../../shared/colors';
import { useGetQuotesByBrokerIdQuery } from '../../services/features/quoteApi/quoteApi';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import quoteHeadCells from './headCells';

/**
 * Component for rendering a table of quotes.
 *
 * @param {object} props - The component's props.
 * @param {Order} props.orderBy - The order to sort by.
 * @param {function} props.setOrderBy - Function to set the sorting order.
 * @param {array} props.quoteStatuses - An array of quote statuses to filter by.
 * @returns {JSX.Element} - The rendered component.
 */
export default function QuotesTable(props: any) {
	const [order, setOrder] = useState<Order>('desc');

	const brokerId = useSelector((state: RootState) => state.authSlice.id);

	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(25);

	const [loadingQuote, setLoadingQuote] = useState(false);

	const quotesQuery = useGetQuotesByBrokerIdQuery(brokerId ?? skipToken, { skip: !brokerId });

	const quoteRows = useMemo(() => {
		// console.log('QUOTE STATUSES', props.quoteStatuses, quotesQuery.data);
		if (quotesQuery.data) {
			return quotesQuery.data
				.filter((quote) =>
					props.quoteStatuses.includes(quote.quotes[0].status.statusNumber)
				)
				.map((quote) => {
					const inceptionDate =
						'policyPeriodStartDate' in quote.quotes[0]
							? String(quote.quotes[0].policyPeriodStartDate)
							: null;
					const lastModifiedDate = String(quote.quotes[0].lastModifiedDate);
					return createQuoteRow(
						quote.id,
						quote.quoteClient.organizationName,
						inceptionDate,
						lastModifiedDate,
						quote.quotes,
						quote.quotes[0].status,
						quote.quoteClient.clientHash
					);
				});
		} else {
			return [];
		}
	}, [quotesQuery.data, quotesQuery.fulfilledTimeStamp]);

	/**
	 * Handle request to sort the table.
	 * @param {event} event - The sort event.
	 * @param {property} property - The property to sort by.
	 */
	const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof QuoteData) => {
		const isAsc = props.orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		props.setOrderBy(property);
	};

	/**
	 * Handle page change.
	 * @param {event} event - The page change event.
	 * @param {newPage} newPage - The new page.
	 */
	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	/**
	 * Handle change in the number of rows per page.
	 * @param {event} event - The event for changing rows per page.
	 */
	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	// const emptyRows = rowsPerPage - Math.min(rowsPerPage, quoteRows.length - page * rowsPerPage);

	/**
	 * Format the quote date.
	 * @param {Date|null} date - The date to format.
	 * @returns {string} - The formatted date.
	 */
	const formatQuoteDate = (date: Date | null) => {
		if (date == null) {
			return 'Date not found';
		}
		let modifiedDate = format(new Date(date), 'dd-MM-yyyy');
		return modifiedDate.endsWith('0001') ? 'Date not found' : modifiedDate;
	};

	const loading = quotesQuery.isLoading || quotesQuery.isUninitialized || quotesQuery.isFetching;

	const shownRows =
		rowsPerPage > 0
			? stableSort<QuoteData>(quoteRows, getComparator(order, props.orderBy)).slice(
					page * rowsPerPage,
					page * rowsPerPage + rowsPerPage
			  )
			: stableSort<QuoteData>(quoteRows, getComparator(order, props.orderBy));

	return (
		<TableContainer component={Paper}>
			<Table aria-labelledby='tableTitle' size='medium' aria-label='enhanced table'>
				<QuoteTableHead
					order={order}
					orderBy={props.orderBy}
					onRequestSort={handleRequestSort}
					rowCount={quoteRows.length}
				/>
				<TableBody>
					{loading ? (
						<TableRow>
							<TableCell colSpan={quoteHeadCells.length} align='center'>
								<CircularProgress sx={{ color: colours.primary.main }} />
							</TableCell>
						</TableRow>
					) : quoteRows.length > 0 ? (
						shownRows.map((quoteRow, index) => {
							return (
								<TableRow key={`table-row-${index}-quote-${quoteRow.id}`}>
									<TableCell
										component='th'
										scope='row'
										align='left'
										width={'20%'}
									>
										{quoteRow.insuredName}
									</TableCell>
									<TableCell width={'15%'} align='left'>
										{quoteRow.inceptionDate
											? formatQuoteDate(new Date(quoteRow.inceptionDate))
											: ''}
									</TableCell>
									<TableCell width={'15%'} align='left'>
										{quoteRow.modifiedDate
											? formatQuoteDate(new Date(quoteRow.modifiedDate))
											: ''}
									</TableCell>
									<TableCell width={'30%'} align='left' colSpan={2}>
										<Stack spacing={1}>
											{quoteRow.quotes.map((q, i) => {
												return (
													<Grid
														container
														key={`quote-${q.id}`}
														width={'100%'}
														alignItems={'center'}
														{...(i !== quoteRow.quotes.length - 1 && {
															pb: '1em',
														})}
													>
														<Grid item xs={7} pr='1em'>
															{`${
																q.quoteProducts.length === 0 &&
																q.quotePackages.length === 0
																	? 'No products'
																	: q.quoteProducts.length > 0
																	? q.quoteProducts[0]?.name.trim()
																	: q.quotePackages[0]?.name.trim()
															} (${q.id})`}
														</Grid>
														<Grid item xs={4}>{q.status.name}</Grid>
													</Grid>
												);
											})}
										</Stack>
									</TableCell>
									{/* <TableCell width={'15%'} align='left'>
										{quoteRow.status}
									</TableCell> */}
									<TableCell width={'10%'} align='center'>
										{quoteRow.id != null && quoteRow.id !== 'loading' && (
											<LoadQuoteButton
												quoteRow={quoteRow}
												setLoadingQuote={setLoadingQuote}
												loadingQuote={loadingQuote}
											/>
										)}
									</TableCell>
								</TableRow>
							);
						})
					) : (
						<TableRow>
							<TableCell colSpan={quoteHeadCells.length} align='center'>
								<Typography variant='subtitle1'>{`No ${
									props.type ?? 'quotes'
								} found`}</Typography>
							</TableCell>
						</TableRow>
					)}
				</TableBody>
				<TableFooter
					sx={{
						'& .MuiTableRow-root': {
							'& .MuiTableCell-root': {
								'& .MuiToolbar-root': {
									'& .MuiTablePagination-displayedRows': {
										padding: 0,
										fontSize: '14px',
										color: '#555454',
										fontWeight: 600,
									},
									'& .MuiTablePagination-selectLabel': {
										padding: 0,
										fontSize: '14px',
										color: '#555454',
										fontWeight: 600,
									},
									'& .tableButtons': {
										display: 'flex',
									},
								},
							},
						},
					}}
				>
					<TableRow>
						<TablePagination
							rowsPerPageOptions={[25, 50, 100, { label: 'All', value: -1 }]}
							count={quoteRows.length}
							rowsPerPage={rowsPerPage}
							page={page}
							SelectProps={{
								inputProps: {
									'aria-label': 'Rows per page',
								},
								native: true,
							}}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
							ActionsComponent={TablePaginationActions}
						/>
					</TableRow>
				</TableFooter>
			</Table>
		</TableContainer>
	);
}
